import { Counters } from 'types';

import { arrayIntersection, getMapEntityFullPath } from 'utils';

import { Entities } from '../types';

const moveOldParentCounters = (
  id: number,
  count: number,
  movingCount: number,
  newParentPath: number[],
  isParentsRelated: boolean
) => {
  // as recursion goes from bottom to top need limit unrelated path ids change
  const movingDiff = newParentPath.includes(id) ? count : count - movingCount;

  return isParentsRelated ? movingDiff : count - movingCount;
};

const moveNewParentCounters = (
  id: number,
  count: number,
  movingCount: number,
  oldParentPath: number[],
  isParentsRelated: boolean
) => {
  // as recursion goes from bottom to top need limit unrelated path ids change
  const movingDiff = oldParentPath.includes(id) ? count : count + movingCount;

  return isParentsRelated ? movingDiff : count + movingCount;
};

export const processCounters = (
  entities: Entities,
  counters: Counters,
  parentId: number,
  processFunc: (id: number, count: number) => number
): Counters => {
  const grandParentId = entities[parentId]?.parentIDs?.[0];
  const count = counters[parentId];

  return {
    [parentId]: processFunc(parentId, count),
    ...(grandParentId &&
      processCounters(entities, counters, grandParentId, processFunc)),
  };
};

export const insertCounters = (
  prevCounters: Counters,
  newCounters?: Counters | null
) => ({
  ...prevCounters,
  ...(newCounters ?? {}),
});

export const increaseMapEntityObjectCounters = (
  entities: Entities,
  counters: Counters,
  entityId: number,
  layerTemplateId: number,
  objectTemplateId: number
) => {
  const entity = entities[entityId];
  const parentId = entity?.parentIDs?.[0];
  const shouldAddCounter =
    !counters[entityId] && entity?.entity?.templateID === layerTemplateId;
  const shouldIncreaseSingle =
    parentId && entity?.entity?.templateID === objectTemplateId;

  return {
    ...counters,
    ...(shouldAddCounter && { [entity?.entity?.id]: 0 }),
    ...(shouldIncreaseSingle &&
      processCounters(entities, counters, parentId, (_id, value) => value + 1)),
  };
};

export const decreaseMapEntityObjectCounters = (
  entities: Entities,
  counters: Counters,
  entityId: number,
  layerTemplateId: number,
  objectTemplateId: number
) => {
  const entity = entities[entityId];
  const parentId = entity?.parentIDs?.[0];
  const decreasedEntityId = String(entity?.entity?.id);
  const { [decreasedEntityId]: _removedCounter, ...restCounters } = counters;

  const shouldRemoveCounter =
    parentId && entity?.entity?.templateID === layerTemplateId;
  const shouldDecreaseSingle =
    parentId && entity?.entity?.templateID === objectTemplateId;
  const shouldDecreaseMultiple =
    parentId && entity?.entity?.templateID === layerTemplateId;

  const currentCounters = shouldRemoveCounter ? restCounters : counters;

  return {
    ...currentCounters,
    ...(shouldDecreaseSingle &&
      processCounters(
        entities,
        currentCounters,
        parentId,
        (_id, value) => value - 1
      )),
    ...(shouldDecreaseMultiple &&
      processCounters(
        entities,
        currentCounters,
        parentId,
        (_id, value) => value - counters[decreasedEntityId]
      )),
  };
};

export const moveCounter = (
  entities: Entities,
  counters: Counters,
  entityId: number,
  oldParentEntityId: number,
  newParentEntityId: number,
  layerTemplateId: number
) => {
  const entity = entities[entityId];
  const oldParentPath = getMapEntityFullPath(entities, oldParentEntityId);
  const newParentPath = getMapEntityFullPath(entities, newParentEntityId);
  const isParentsRelated =
    arrayIntersection(oldParentPath, newParentPath).length > 1;
  const movingCount =
    entity.entity.templateID === layerTemplateId ? counters[entityId] : 1;

  return {
    ...counters,
    ...processCounters(entities, counters, oldParentEntityId, (_id, count) =>
      moveOldParentCounters(
        _id,
        count,
        movingCount,
        newParentPath,
        isParentsRelated
      )
    ),
    ...processCounters(entities, counters, newParentEntityId, (id, count) =>
      moveNewParentCounters(
        id,
        count,
        movingCount,
        oldParentPath,
        isParentsRelated
      )
    ),
  };
};
